import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";
import helper from "./qbo_AccountingHelper";
import createTranslator from "../../util/translationHelper";
import messages from "./translations/messages";
import {
  userActions,
  apiList,
  methodType,
} from "../../contextProviders/constant";
import { useAppStateProvider } from "../../contextProviders/AppStateProvider";
import authApi from "../../contextProviders/auth-api";
import getToken from "../../util/tokenGenerator";
import { handleTokenRequest } from "../../util/helper";
import useAccountingFeatureFlag from "./useAccountingFeatureFlag";

function useQBO_Accounting(params) {
  const { lang, history } = params;
  const BLOCK = useRef("qbo-accounting");
  const analyticsTrackedRef = useRef(false);
  const { isEnabled: ffPassed, isLoading: ffLoading } =
    useAccountingFeatureFlag({ lang, history });
  const { appState, updateAppState, updateAppStateLocal } =
    useAppStateProvider();
  const { instance, inProgress, accounts } = useMsal();
  const getTokenHandler = () => getToken(instance, inProgress, accounts);
  const aboutResourceLinks = {
    "resource-site": "/site",
    "resource-price": "/price",
    "resource-support": "/support",
  };

  const [statusModalState, dispatchStatusModalState] = useReducer(
    helper.statusModalStateReducer,
    {
      status: null,
      shownAlready: true,
    },
  );

  const [QBOMonerisConnectionStatus, setQBOMonerisConnectionStatus] =
    useState("LOADING");

  const [QBOSubscription, setQBOSubscription] = useState(null);

  const isPending = QBOMonerisConnectionStatus === 'CONNECTED' && (
    QBOSubscription?.status === "PENDING" ||
    QBOSubscription?.status === "AUTH_COMPLETE"
  );

  const translate = useMemo(() => {
    return createTranslator(messages);
  }, [createTranslator, messages]);

  const trackLandingPageViewNoConnection = () => {
    if (analyticsTrackedRef.current) {
      return;
    }
    analyticsTrackedRef.current = true;

    // Fire-and-forget analytics call
    authApi({
      url: apiList[userActions.analyticsTrace],
      method: methodType.post,
      body: {
        event_type: "landing_page_view_no_connection",
        partner_company_code: null,
        partner_account_id: null,
        metadata: null,
      },
      tokenhandler: async () => await handleTokenRequest(getTokenHandler),
      isSecured: true,
    }).catch((error) => {
      // Silently log the error - do not surface to user
      console.error("Analytics tracking failed:", error);
    });
  };

  const getActiveConsent = async () => {
    try {
      const response = await authApi({
        url: apiList[userActions.getUIConnections],
        method: methodType.get,
        tokenhandler: async () => await handleTokenRequest(getTokenHandler),
        isSecured: true,
      });

      const connections = response?.data?.connections || [];
      const subscription = response?.data?.subscription ?? null;

      // Track landing page view if user has no connections
      if (connections.length === 0) {
        trackLandingPageViewNoConnection();
      }

      const qboConnection = connections.find(
        (connection) => connection.partner_company_code === "qbo",
      );

      // Store qboConnection and subscription in appState
      updateAppStateLocal({ qboConnection, qboSubscription: subscription });

      const isConnected = qboConnection?.token_valid ?? false;
      setQBOMonerisConnectionStatus(isConnected ? "CONNECTED" : "DISCONNECTED");
      setQBOSubscription(subscription);

      // Only check eligibility if there is no active connection
      if (!isConnected) {
        updateAppStateLocal({ qboEligibilityLoading: true });
        try {
          await getQboConnectionEligibility();
        } finally {
          updateAppStateLocal({ qboEligibilityLoading: false });
        }
      }
    } catch {
      setQBOMonerisConnectionStatus("DISCONNECTED");
      updateAppStateLocal({ qboEligibilityError: true });
      dispatchStatusModalState({
        type: "setStateFromModalStatus",
        payload: "error",
      });
    }
  };

  const getQboConnectionEligibility = async () => {
    const url = apiList[userActions.qboConnectionEligibility];
    const response = await authApi({
      body: {},
      method: methodType.get,
      url,
      tokenhandler: async () => await handleTokenRequest(getTokenHandler),
      isSecured: true,
      headers: { "api-version": "v1" },
    });

    if (!response?.data || response?.status >= 400) {
      updateAppStateLocal({ qboEligibilityError: true });
      dispatchStatusModalState({
        type: "setStateFromModalStatus",
        payload: "error",
      });
      return;
    }

    updateAppStateLocal({ qboEligibilityError: false });
    const eligibility = response?.data?.eligibility ?? null;
    const eligibilitySubscription = response?.data?.subscription ?? null;
    updateAppStateLocal({ qboConnectionEligibility: eligibility });

    // Update subscription from eligibility if available (may contain latest OMS status)
    if (eligibilitySubscription) {
      updateAppStateLocal({ qboSubscription: eligibilitySubscription });
    }
  };

  useEffect(() => {
    dispatchStatusModalState({ type: "initStateFromLocalStorage" });
  }, [dispatchStatusModalState]);

  // Open eligibility modal if redirected from a subpage guard
  useEffect(() => {
    const stored = sessionStorage.getItem("qboEligibilityRedirect");
    if (stored) {
      try {
        const { eligibility } = JSON.parse(stored);
        if (eligibility) {
          updateAppStateLocal({
            qboConnectionEligibility: eligibility,
            eligibilityModalOpen: true,
          });
        }
      } catch {
        // ignore parse errors
      }
      sessionStorage.removeItem("qboEligibilityRedirect");
    }
  }, []);

  useEffect(() => {
    if (appState?.uid && ffPassed) {
      getActiveConsent();
    }
  }, [appState.uid, ffPassed]);

  // Watch for refreshConnection flag to refresh connection status after disconnect
  useEffect(() => {
    if (appState?.refreshConnection) {
      getActiveConsent();
      updateAppStateLocal({ refreshConnection: false });
    }
  }, [appState?.refreshConnection]);

  const disconnectedStatusModal = appState?.disconnectedStatusModal || false;
  const disconnectErrorStatusModal =
    appState?.disconnectErrorStatusModal || false;
  const changePlanSuccess = appState?.changePlanSuccess || false;
  const cancelSubscriptionSuccess =
    appState?.cancelSubscriptionSuccess || false;
  const reactivateSuccess = appState?.reactivateSuccess || false;

  const clearStatusFlag = useCallback(
    (flag) => {
      updateAppStateLocal({ [flag]: false });
    },
    [updateAppStateLocal],
  );

  return {
    BLOCK,
    aboutResourceLinks,
    translate,
    statusModalState,
    QBOMonerisConnectionStatus,
    QBOSubscription,
    isPending,
    disconnectedStatusModal,
    disconnectErrorStatusModal,
    changePlanSuccess,
    cancelSubscriptionSuccess,
    reactivateSuccess,
    clearStatusFlag,
    dispatchStatusModalState,
    ffPassed,
    ffLoading,
  };
}

const Hook = {
  useQBO_Accounting,
};

export default Hook;
